import clsx from 'clsx'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import SettingsIcon from '@/assets/icons/settings.svg?react'
import MessageDotsIcon from '@/assets/icons/message-dots.svg?react'
import LogOutIcon from '@/assets/icons/log-out.svg?react'
import { ArrowUpRight, Tools, SyncProgress, FileClock } from '@/assets/icons'
import { kLinkPrivacyPolicyUrl, kLinkTermsOfServiceUrl, kPaxtonHelpCenterUrl } from '@/constants/constants-links'
import { MenuItemGroup, MenuSeparator } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { Button } from '@/components/button/Button'
import Tooltip from '@/components/ui/tooltip/Tooltip'
import { useIsTruncated } from '@/components/ui/tooltip/useIsTruncated'
import { AccountMenuViewOrganizationCard } from '@/account/components/AccountMenuViewOrganizationCard'
import { PostHogDemoMenuItem } from './PostHogDemoMenuItem'

type AccountMenuRevampContentProps = {
  /** Resolved header label (email, falling back to display name) */
  email: string
  isOrgAccount: boolean
  isOrgAdmin: boolean
  isPaxtonUserAccount: boolean
  orgName: string
  memberCount: number
  /** Highlights the Settings item when the settings route is active */
  settingsSelected: boolean
  usageSelected: boolean
  onUsage?: () => void
  onOpenRoster: () => void
  /** Provided for org admins only; renders the "Invite members" link */
  onInviteMembers?: () => void
  onSettings: () => void
  onSupport: () => void
  onSignOut: () => void
  /** Dev-only token logger; provided only outside production */
  onLogToken?: () => void
  /** Collapses the mobile sidebar after any selection, matching the current menu */
  onCloseMobileSidebar: () => void
  /** A PWA update is waiting; the footer offers the apply action so the avatar indicator is not a dead end */
  updateAvailable: boolean
  /** Disables the footer update action while an apply is in flight */
  isApplying: boolean
  onApplyUpdate: () => void
}

/**
 * Presentational content for the settings-revamp account menu (rendered inside the shared MenuContent).
 * Matches the Figma/prototype layout: a bordered email header on top, an org card for org accounts, the
 * account actions, external links, and the version bar. All logic, data fetching, and modal state stay in
 * the SidebarAccountMenu container; this component takes primitives and callbacks only.
 */
export function AccountMenuRevampContent({
  email,
  isOrgAccount,
  isOrgAdmin,
  isPaxtonUserAccount,
  orgName,
  memberCount,
  settingsSelected,
  usageSelected,
  onUsage,
  onOpenRoster,
  onInviteMembers,
  onSettings,
  onSupport,
  onSignOut,
  onLogToken,
  onCloseMobileSidebar,
  updateAvailable,
  isApplying,
  onApplyUpdate,
}: AccountMenuRevampContentProps) {
  const { ref: emailHeaderRef, isTruncated: isEmailHeaderTruncated } = useIsTruncated<HTMLParagraphElement>()

  const select = (action: () => void) => () => {
    action()
    onCloseMobileSidebar()
  }

  const openExternal = (url: string) => () => {
    window.open(url, '_blank')
    onCloseMobileSidebar()
  }

  return (
    <>
      {/* Email header: bordered row at the top of the menu. */}
      <div className="flex min-h-9 items-center border-b border-border-default bg-surface-content px-4 py-1.5">
        <Tooltip title={isEmailHeaderTruncated ? email : ''} placement="top">
          <p ref={emailHeaderRef} className="min-w-0 truncate text-xs font-medium leading-4 text-text-secondary">
            {email}
          </p>
        </Tooltip>
      </div>

      {/* Org card (org accounts only); admins also get the Invite members link. */}
      {isOrgAccount && (
        <>
          <AccountMenuViewOrganizationCard
            orgName={orgName}
            memberCount={memberCount}
            onViewOrganization={onOpenRoster}
            onInviteMembers={isOrgAdmin ? onInviteMembers : undefined}
          />
          <MenuSeparator />
        </>
      )}

      <MenuItemGroup>
        <MenuItem label="Settings" icon={SettingsIcon} selected={settingsSelected} onSelect={select(onSettings)} />
        {onUsage && <MenuItem label="Usage" icon={FileClock} selected={usageSelected} onSelect={select(onUsage)} />}
        <MenuItem label="Chat with support" icon={MessageDotsIcon} onSelect={select(onSupport)} />
        <MenuItem label="Sign out" icon={LogOutIcon} onSelect={select(onSignOut)} />
        {isPaxtonUserAccount && <PostHogDemoMenuItem onSelect={onCloseMobileSidebar} />}
        {onLogToken && <MenuItem label="Log token (dev only)" icon={Tools} onSelect={select(onLogToken)} />}
      </MenuItemGroup>

      <MenuSeparator />
      <MenuItemGroup>
        <MenuItem label="Help Center" trailingIcon={ArrowUpRight} onSelect={openExternal(kPaxtonHelpCenterUrl)} />
        <MenuItem label="Privacy Policy" trailingIcon={ArrowUpRight} onSelect={openExternal(kLinkPrivacyPolicyUrl)} />
        <MenuItem label="Terms of Service" trailingIcon={ArrowUpRight} onSelect={openExternal(kLinkTermsOfServiceUrl)} />
      </MenuItemGroup>

      {/* Version bar; when a PWA update is waiting, the footer also offers the apply action so the
          avatar update indicator always has a way to act on it (mirrors the current menu). */}
      <div className={clsx('flex items-center bg-surface-subtle px-4 py-2', updateAvailable ? 'gap-2.5' : 'h-7')}>
        <span className={clsx('min-w-0 flex-1 text-[10.4px] font-normal leading-[1.077em] text-brand-neutral-700', !updateAvailable && 'block')}>
          v{APP_VERSION}
        </span>
        {updateAvailable && (
          // Same composition as Invite members: Item owns menu keyboard/select; Button is the visual.
          <DropdownMenu.Item
            disabled={isApplying}
            onSelect={() => onApplyUpdate()}
            // Item owns focus; keep its own chrome clear and drive the button fill from highlighted.
            className="group rounded p-0 outline-hidden data-[highlighted]:bg-transparent focus:bg-transparent"
          >
            <Button
              type="button"
              variant="outlined"
              color="primary"
              size="xs"
              // Footer is brand-neutral-100, so bump hover/highlight one token darker to show.
              className="hover:!bg-brand-neutral-200 active:!bg-brand-neutral-200 group-data-[highlighted]:!bg-brand-neutral-200"
              leadingIcon={<SyncProgress className="h-3.5 w-3.5" />}
              disabled={isApplying}
            >
              Update version
            </Button>
          </DropdownMenu.Item>
        )}
      </div>
    </>
  )
}
